import { Box, Dialog, Stack, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import CommonLayout from "../../layout/CommonLayout";
import StyledButton from "../../ui/styledButton";
import StyledFooter from "../../ui/StyledFooter";
import StyledInput from "../../ui/styledInput";
import { Transition } from "../../utils/DialogAnimation";
import { useEditUser } from "../../hooks/mutations/useUserMutation";

const emptyValue = (value) => (!value || value === "-" || value === "_") ? "" : value;

export default function EditUser({ open, user, onClose }) {
  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      PaperProps={{ sx: { bgcolor: "#27292F", backgroundImage: "none" } }}
    >
      {user && <EditUserForm key={user._id} user={user} onClose={onClose} />}
    </Dialog>
  );
}

function EditUserForm({ user, onClose }) {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      username: emptyValue(user.username),
      mobile: emptyValue(user.mobile),
      email: emptyValue(user.email),
      pan: emptyValue(user.pan),
    },
  });

  const { mutate: updateUser, isPending } = useEditUser({
    onSuccess: () => {
      toast.success("User updated successfully");
      onClose();
    },
    onError: (error) => {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "";
      if (/duplicate|E11000/i.test(String(message))) {
        toast.error("Phone number or email is already in use");
        return;
      }
      toast.error(message || "Failed to update user");
    },
  });

  const onSubmit = (data) => {
    updateUser({
      userId: user._id,
      data: {
        username: data.username.trim(),
        mobile: data.mobile.trim(),
        email: data.email.trim() || null,
        pan: data.pan.trim(),
      },
    });
  };

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CommonLayout header="Edit User" onClick={onClose}>
          <Field label="Name" error={errors.username?.message}>
            <Controller
              name="username"
              control={control}
              rules={{ maxLength: { value: 80, message: "Name is too long" } }}
              render={({ field }) => (
                <StyledInput {...field} placeholder="Enter name" />
              )}
            />
          </Field>

          <Field label="Phone number" error={errors.mobile?.message}>
            <Controller
              name="mobile"
              control={control}
              rules={{
                required: "Phone number is required",
                pattern: {
                  value: /^\+?[0-9]{8,15}$/,
                  message: "Enter a valid phone number",
                },
              }}
              render={({ field }) => (
                <StyledInput {...field} placeholder="Enter phone number" />
              )}
            />
          </Field>

          <Field label="Email" error={errors.email?.message}>
            <Controller
              name="email"
              control={control}
              rules={{
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Enter a valid email",
                },
              }}
              render={({ field }) => (
                <StyledInput {...field} placeholder="Enter email" />
              )}
            />
          </Field>

          <Field label="PAN" error={errors.pan?.message}>
            <Controller
              name="pan"
              control={control}
              rules={{ maxLength: { value: 20, message: "PAN is too long" } }}
              render={({ field }) => (
                <StyledInput {...field} placeholder="Enter PAN" />
              )}
            />
          </Field>
        </CommonLayout>
        <StyledFooter width="100">
          <Stack direction="row" spacing={2}>
            <StyledButton
              variant="secondary"
              width="120"
              style={{ height: "45px" }}
              type="button"
              onClick={onClose}
            >
              Cancel
            </StyledButton>
            <StyledButton
              variant="primary"
              width="150"
              style={{ height: "45px" }}
              type="submit"
              disabled={isPending}
            >
              {isPending ? "Saving..." : "Save"}
            </StyledButton>
          </Stack>
        </StyledFooter>
      </form>
    </Box>
  );
}

function Field({ label, error, children }) {
  return (
    <Box>
      <Typography
        variant="subtitle2"
        align="left"
        sx={{ color: "primary.contrastText", mb: 1 }}
      >
        {label}
      </Typography>
      {children}
      {error && <span style={{ color: "red" }}>{error}</span>}
    </Box>
  );
}
